import {Storage} from 'storages/storage';
import {isPlayerOnDoublePosition} from 'helpers/player-helper';
import Action from 'actions';
import Const from 'constants';

class PlayersPoolBuilder {
    constructor(pool) {
        const {
            sportConfig,
            projectionPointsType,
            playersData: {filters: playersFilter},
        } = Action.getState();
        const that = this;

        this.tasks = [];
        this.CStore = new Storage();
        this.Builder = {
            set: {
                pool(arr) {
                    const pool = [];
                    arr.map((player) => {
                        // If a player isn't in teams which were chosen from a schedule of games, pass him.
                        if (sportConfig.useGameFilter() && !inArray(player.team, playersFilter.games)) {
                            return;
                        }
                        // If a player has a negative number of points, pass him.
                        if (player[projectionPointsType.getProjectionPointsType()] < 0) {
                            return;
                        }
                        pool.push(player);
                    });
                    that.CStore.set('pool', pool);
                },
            },
            groupBy: {
                allowedPositions(sharedByPosition) {
                    const pool = [];
                    const positions = sportConfig.getConfigProp('positions');
                    // Separation of players on all allowed position from a config.
                    Object.keys(positions).map(function (pos) {
                        const posObj = positions[pos];
                        // If an array with allowed positions is empty, move to next iteration.
                        if (isEmpty(posObj.allowedPositions)) {
                            return;
                        }
                        sharedByPosition.map(function (player) {
                            if (inArray(Number(player.position), posObj.allowedPositions)) {
                                const newPlayer = {...player};
                                newPlayer.position = Number(pos);
                                pool.push(newPlayer);
                            }
                        });
                    });
                    that.CStore.set('allowedPositions', pool);
                },
                position(player) {
                    const pool = that.CStore.get('position', []);
                    const positionName = player.positionName.split('/');
                    player.position.map((pos, i) => {
                        const newPlayer = {...player};
                        newPlayer.position = Number(pos);
                        newPlayer.positionName = positionName[i];
                        newPlayer.uniqueId = newPlayer.playerId;
                        if (isPlayerOnDoublePosition(player)) {
                            newPlayer.uniqueId += Const.LP_ITEM_DELIMITER + pos;
                        }
                        pool.push(newPlayer);
                    });
                    that.CStore.set('position', pool);
                },
                playerId(player) {
                    let pool = that.CStore.get('playerId', {});
                    pool[player.playerId] = player;
                    that.CStore.set('playerId', pool);
                },
                externalId(player) {
                    let pool = that.CStore.get('externalId', {});
                    pool[player.externalId] = player;
                    that.CStore.set('externalId', pool);
                },
                doublePosition(player) {
                    // If a player stands on different positions, push him to an appropriate array.
                    if (isPlayerOnDoublePosition(player)) {
                        const pool = that.CStore.get('doublePosition', []);
                        pool.push(player);
                        that.CStore.set('doublePosition', pool);
                    }
                },
                gameTeamPosName(player) {
                    const pool = that.CStore.get('gameTeamPosName', {});

                    const gameId = player.getGameKey();
                    if (!pool.hasOwnProperty(gameId)) {
                        pool[gameId] = {
                            [player.team]: {
                                name: player.team,
                                positions: {},
                            },
                            [player.opponent]: {
                                name: player.opponent,
                                positions: {},
                            },
                        };
                    }
                    if (!pool[gameId][player.team].positions.hasOwnProperty(player.positionName)) {
                        pool[gameId][player.team].positions[player.positionName] = [];
                    }
                    pool[gameId][player.team].positions[player.positionName].push(player);

                    that.CStore.set('gameTeamPosName', pool);
                },
                teamAndPosition(player) {
                    const pool = that.CStore.get('teamAndPosition', {});

                    /**
                     * Attention! It isn't possible that one team will play two or more games in one slate,
                     * so we can safety set the opponent team from a first player.
                     */
                    if (!pool.hasOwnProperty(player.team)) {
                        pool[player.team] = {
                            opponent: player.opponent,
                            positions: {},
                        };
                    }

                    let positionName = player.positionName.split('/');
                    player.position.map(function (pos, i) {
                        const pName = positionName[i];
                        pool[player.team].positions[pName] ??= [];
                        pool[player.team].positions[pName].push(player);
                    });

                    that.CStore.set('teamAndPosition', pool);
                },
                property(player, property, filterFunc = null) {
                    if (player.hasOwnProperty(property)) {
                        const pool = that.CStore.get('property', {});

                        /**
                         * Attention! It isn't possible that one team will play two or more games in one slate,
                         * so we can safety set an opponent team from a first player.
                         */
                        if (!pool.hasOwnProperty(property)) {
                            pool[property] = {};
                        }

                        if (typeof filterFunc === 'function') {
                            if (filterFunc(player)) {
                                if (!pool[property].hasOwnProperty(player[property])) {
                                    pool[property][player[property]] = [];
                                }
                                pool[property][player[property]].push(player);
                            }
                        } else {
                            if (!pool[property].hasOwnProperty(player[property])) {
                                pool[property][player[property]] = [];
                            }
                            pool[property][player[property]].push(player);
                        }

                        that.CStore.set('property', pool);
                    }
                },
            },
        };
        this.Builder.set.pool(cp(pool));
    }

    addTaskOnScenario(scenarioName) {
        try {
            if (!this.Builder.groupBy.hasOwnProperty(scenarioName)) {
                throw new Error('PlayersPoolBuilder: scenario ' + scenarioName + ' does not exist');
            }
            this.tasks.push({scenario: scenarioName});
        } catch (e) {
            console.error(e);
        }
    }

    addTaskOnProperty(propName, filterFunc = null) {
        this.tasks.push({
            scenario: 'property',
            propName,
            filterFunc,
        });
    }

    execTasks() {
        try {
            if (isEmpty(this.tasks)) {
                throw new Error('PlayersPoolBuilder: tasks have not been added');
            }

            let execAllowedPositionsScenario = false;

            const that = this;
            const pool = this.CStore.get('pool', []);

            pool.map((player) => {
                that.tasks.map((task) => {
                    if ('property' === task.scenario) {
                        that.Builder.groupBy[task.scenario](player, task.propName, task.filterFunc);
                    } else if ('allowedPositions' === task.scenario) {
                        execAllowedPositionsScenario = true;
                    } else {
                        that.Builder.groupBy[task.scenario](player);
                    }
                });
            });

            const positions = this.CStore.get('position', []);
            if (execAllowedPositionsScenario && !isEmpty(positions)) {
                this.Builder.groupBy.allowedPositions(positions);
            }

            const result = {};
            this.tasks.map((task) => {
                if ('property' === task.scenario) {
                    result[task.scenario] = function (property, defaultValue = {}) {
                        const list = cp(that.CStore.get(task.scenario, {}));
                        if (list.hasOwnProperty(property)) {
                            return list[property];
                        }
                        return defaultValue;
                    };
                } else {
                    result[task.scenario] = function () {
                        return cp(that.CStore.get(task.scenario, []));
                    };
                }
            });

            return {
                groupedBy: result,
            };
        } catch (e) {
            console.error(e);
        }
    }
}

export {PlayersPoolBuilder};
